import { Box, CircularProgress, FormControlLabel, Switch } from '@mui/material'
import {
  useAccountGovernanceMaps,
  useUpdateAccountGovernanceMaps
} from 'features/account/hooks/useAccountGovernanceMaps'
import { Container } from './common.styles'
import { Typography } from '@carto/meridian-ds/components'
import { FormattedMessage } from 'react-intl'
import intl from 'app/localization/intl'
import useUserPermissions from 'features/account/state/useUserPermissions'

function AccountGovernanceMaps() {
  const { data, isLoading, error, refetch } = useAccountGovernanceMaps()
  const { mutateAsync, isLoading: isUpdating } = useUpdateAccountGovernanceMaps()
  const { sharingMapsAvailable, exportPdfReportAvailable, exportViewportDataAvailable } = useUserPermissions()

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Box color="error.main">{error}</Box>
  }

  if (!data) return null

  const handleAllowEditorsToCreateChange = (checked: boolean) => {
    mutateAsync({ ...data, allowUsersToCreatePublicMaps: checked }).then(() => {
      refetch()
    })
  }

  const handleAllowEditorsEnableDisableDownloadPdfChange = (checked: boolean) => {
    mutateAsync({ ...data, allowUsersToManagePDFReports: checked }).then(() => {
      refetch()
    })
  }

  const handleAllowEditorsEnableDisableExportingViewportDataChange = (checked: boolean) => {
    mutateAsync({ ...data, allowUsersToManageViewportDataExport: checked }).then(() => {
      refetch()
    })
  }

  return (
    <>
      {sharingMapsAvailable && (
        <Container divider={exportPdfReportAvailable || exportViewportDataAvailable}>
          <Typography variant="subtitle2" color="textSecondary" fontWeight={600} mb={1}>
            <FormattedMessage id="account.cards.governance.maps.publicMaps.title" />
          </Typography>
          <Box display="flex" alignItems="center" gap={1} pt={1}>
            <FormControlLabel
              label={<FormattedMessage id="account.cards.governance.maps.publicMaps.label" />}
              labelPlacement="end"
              value="allowEditorsToCreate"
              disabled={isLoading || isUpdating}
              control={
                <Switch
                  onChange={(_, checked) => handleAllowEditorsToCreateChange(checked)}
                  checked={data?.allowUsersToCreatePublicMaps ?? false}
                  aria-label={`Enable ${intl.formatMessage({
                    id: 'account.cards.governance.maps.publicMaps.label'
                  })}`}
                  tabIndex={0}
                />
              }
            />
          </Box>
        </Container>
      )}
      {exportPdfReportAvailable && (
        <Container divider={exportViewportDataAvailable}>
          <Typography variant="subtitle2" color="textSecondary" fontWeight={600} mb={1}>
            <FormattedMessage id="account.cards.governance.maps.downloadPdf.title" />
          </Typography>
          <Box display="flex" alignItems="center" gap={1} pt={1}>
            <FormControlLabel
              label={<FormattedMessage id="account.cards.governance.maps.downloadPdf.label" />}
              labelPlacement="end"
              value="allowUsersToManagePDFReports"
              disabled={isLoading || isUpdating}
              control={
                <Switch
                  checked={data?.allowUsersToManagePDFReports ?? false}
                  onChange={(_, checked) => handleAllowEditorsEnableDisableDownloadPdfChange(checked)}
                />
              }
            />
          </Box>
        </Container>
      )}
      {exportViewportDataAvailable && (
        <Container divider={false} sx={{ paddingBottom: 1 }}>
          <Typography variant="subtitle2" color="textSecondary" fontWeight={600} mb={1}>
            <FormattedMessage id="account.cards.governance.maps.exportingViewportData.title" />
          </Typography>
          <Box display="flex" alignItems="center" gap={1} pt={1}>
            <FormControlLabel
              label={<FormattedMessage id="account.cards.governance.maps.exportingViewportData.label" />}
              labelPlacement="end"
              value="allowUsersToManageViewportDataExport"
              disabled={isLoading || isUpdating}
              control={
                <Switch
                  checked={data?.allowUsersToManageViewportDataExport ?? false}
                  onChange={(_, checked) => handleAllowEditorsEnableDisableExportingViewportDataChange(checked)}
                />
              }
            />
          </Box>
        </Container>
      )}
    </>
  )
}

export default AccountGovernanceMaps
